'use client';

import './styles/@theme';
import { useState, useRef, useEffect } from 'react';
import { formatNumberDefault } from '@/utils/numbers/format-number';
import type { TierWinner } from '@/types/zones/slices/site-wide-jackpot-slice';

const MOBILE_ANIMATION_DURATION = 6;
const DESKTOP_ANIMATION_DURATION = 4;

export const ScrollingList = ({
    tierWinners,
    isMobileOrTablet
}: {
    tierWinners: TierWinner[];
    isMobileOrTablet?: boolean;
}) => {
    const [displayedTierWinners, setDisplayedTierWinners] = useState(tierWinners);
    const isInitialRender = useRef(true);
    const scrollingTextRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (scrollingTextRef.current) {
            scrollingTextRef.current.style.animationDuration = `${
                (isMobileOrTablet ? MOBILE_ANIMATION_DURATION : DESKTOP_ANIMATION_DURATION) * tierWinners.length
            }s`;
        }
    }, [tierWinners.length, isMobileOrTablet]);

    useEffect(() => {
        // Skip the effect logic if it's the initial render
        if (isInitialRender.current) {
            isInitialRender.current = false;
            return;
        }

        const element = scrollingTextRef.current;

        // In this way the animation is fading out and after that it resets to the initial position
        if (element) {
            element.classList.add('fade-out');

            setTimeout(() => {
                setDisplayedTierWinners(tierWinners);
                element.classList.remove('fade-out');
            }, 1000);
        }
    }, [tierWinners]);

    if (!displayedTierWinners.length) {
        return null;
    }

    return (
        <div ref={scrollingTextRef} data-testid="scrolling-text" className="scrolling-text scrolling-animation">
            {displayedTierWinners.map((tierWinner, index) => (
                <span key={`${tierWinner.tierName}-${tierWinner.username}-${index}`}>
                    <span className="bold">
                        {tierWinner.username} just won &nbsp;{formatNumberDefault(tierWinner.amount)}&nbsp; on the
                        &nbsp;{tierWinner.tierName} Jackpot&nbsp;
                    </span>
                    <span className="time-ago">{tierWinner.timeAgo}</span>
                    <span className="separator" />
                </span>
            ))}
        </div>
    );
};
